import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Chip,
  Typography,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUserScreeningFiles, uploadPdfScreeningFiles, deleteScreeningFile } from '../../Utils/functions';
import FileUpload from "../../Components/FIleUpload/FileUpload";
import LinearProgress from '@mui/material/LinearProgress';

const FileSelector = ({ isOpen, onClose, onFileSelect, selectedFiles }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');


  useEffect(() => {
    if (isOpen) {
      loadUserFiles();
    }
  }, [isOpen]);

  const loadUserFiles = async () => {
    setLoading(true);
    try {
      const data = await getUserScreeningFiles();
      setFiles(data.files);
    } catch (err) {
      setError(err.message || 'Failed to load files.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (uploadedFiles) => {

    setUploading(true);
    setError('');

    try {
      const data = await uploadPdfScreeningFiles(uploadedFiles);
      await loadUserFiles();
      console.log(data.files);

      //select all uploaded files
      for (const file of data.files) {
        onFileSelect(file, false);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (file) => {
    setLoading(true);
    setError('');
    deleteScreeningFile(file)
      .then(() => {
        // Remove the deleted file from the list
        setFiles(files.filter(f => f.id !== file.id));
        onFileSelect({ id: file.id, fileName: file.fileName }, true);
      })
      .catch(err => {
        setError(err.message || 'Failed to delete file.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isFileSelected = (file) => {
    return selectedFiles.some(selectedFile => selectedFile.id === file.id);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Select Files for Screening</DialogTitle>
      <DialogContent dividers>
        {/* Selected Files Chips */}
        {selectedFiles.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ width: '100%', mb: 1 }}>
              Selected Files:
            </Typography>
            {selectedFiles.map((file) => (
              <Chip
                key={file.id}
                label={file.fileName}
                onDelete={() => onFileSelect(file, true)}
                color="primary"
              />
            ))}
          </Box>
        )}

        <FileUpload onUpload={handleUpload} isAdmin={true} />
        {uploading && (
          <Box sx={{ width: '100%', mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
              Uploading files...
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Previously Uploaded Files */}
        <Typography variant="h6" gutterBottom>
          Select from Previous Uploads
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {files.length === 0 && (
              <ListItem>
                <ListItemText primary="No previously uploaded files found." />
              </ListItem>
            )}
            {files.map(file => (
              <ListItem
                key={file.id}
                button
                onClick={() => onFileSelect(file, isFileSelected(file))}
                selected={isFileSelected(file)}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent ListItem onClick from firing
                      handleDelete(file);
                    }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                }
              >
                <ListItemText primary={file.fileName} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Done
        </Button>
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default FileSelector;